import React, { useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import InterviewUpgradeGate from './InterviewUpgradeGate';
import './InterviewBrowse.css';
import './InterviewWritten.css';

/**
 * What the Feedback and Review pages share: which finished paper or
 * interview they're about (run ids, handed over in route state, which a
 * reload keeps), the way back, and the pair of tabs that link them. The two
 * are separate pages on purpose: Review is the model answers, Feedback is
 * the student's own answers and marks.
 */
export function useReportContext() {
  const { workspace: workspaceSlug, leafKey, paperId } = useParams();
  const location = useLocation();
  const isLive = paperId === 'live';
  const runIds = location.state?.runIds || [];
  const mode = location.state?.mode || (isLive ? 'live' : 'written');
  const courseUrl = `/interviews/${workspaceSlug}/${leafKey}`;
  const base = `${courseUrl}/${paperId}`;
  return {
    workspaceSlug, leafKey, paperId, isLive, runIds, mode, courseUrl, base,
    linkState: { runIds, mode },
  };
}

/** Anchors (#q-2) are set by links between the two pages; React Router
 * doesn't scroll to them, so do it once the questions are on the page. */
export function useScrollToHash(ready) {
  const { hash } = useLocation();
  useEffect(() => {
    if (!ready || !hash) return;
    const el = document.getElementById(hash.slice(1));
    if (el?.scrollIntoView) el.scrollIntoView({ block: 'start' });
  }, [ready, hash]);
}

export default function ReportShell({ active, title, status, children }) {
  const ctx = useReportContext();
  const back = ctx.isLive
    ? <Link to={ctx.courseUrl}>Back to the course</Link>
    : <Link to={ctx.base}>Back to the paper</Link>;

  let body = children;
  if (status === 'loading') body = <div className="ib__state">Loading your answers…</div>;
  if (status === 'upgrade') body = <InterviewUpgradeGate />;
  if (status === 'no-runs') {
    body = (
      <div className="ib__state">
        This page opens from a finished {ctx.mode === 'written' ? 'paper' : 'interview'}. {back}
      </div>
    );
  }
  if (status === 'error') {
    body = <div className="ib__state ib__state--error">Could not load your answers. {back}</div>;
  }

  return (
    <div className="ib">
      <div className="ib__crumbs">
        <Link to="/apps">Apps</Link> / <Link to={ctx.courseUrl}>Course</Link>
        {!ctx.isLive && <> / <Link to={ctx.base}>Paper</Link></>}
      </div>
      <h1 className="ib__title">{title}</h1>
      <nav className="ir-tabs" aria-label="Feedback and review">
        <Link
          to={`${ctx.base}/feedback`}
          state={ctx.linkState}
          className={`ir-tab${active === 'feedback' ? ' ir-tab--active' : ''}`}
          aria-current={active === 'feedback' ? 'page' : undefined}
        >
          Feedback
        </Link>
        <Link
          to={`${ctx.base}/review`}
          state={ctx.linkState}
          className={`ir-tab${active === 'review' ? ' ir-tab--active' : ''}`}
          aria-current={active === 'review' ? 'page' : undefined}
        >
          Review
        </Link>
      </nav>
      {body}
    </div>
  );
}
